


import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../../../styles/devotionalVideoList.css';
import { FaYoutube } from "react-icons/fa";
import Skeleton from '../../pages/skelton.jsx';
import DevotionalBreadCrumb from '../devotionalBreadCrumb.jsx';
import { Helmet } from 'react-helmet-async';
import { DeviceContext } from '../index.jsx';
import PageAds from '../../common/pageAds.jsx';

const DevotionalVideoList = ({ data }) => {
  const ssrData = data?.[0]?.videoListPage || {};
  const ssrPosts = ssrData?.videoListData || [];
  const ssrError = ssrData?.videoListDataError || null;
  const ssrLimit = ssrData?.videoListLimit || 0;
  const ssrCatId = ssrData?.cat_id;

  const {mobile,setMobile,containerRef} = useContext(DeviceContext);

  const location = useLocation();
  const pathname = location.pathname;

  const currentCatId = pathname.substring(
    pathname.lastIndexOf("-") + 1,
    pathname.lastIndexOf(".html")
  );
  const initBread = pathname.substring(
    pathname.lastIndexOf("/") + 1,
    pathname.lastIndexOf("-")
  );

  const isInitialSSRCategory =
    ssrPosts.length > 0 && String(ssrCatId) === currentCatId;

  const [posts, setPosts] = useState(isInitialSSRCategory ? ssrPosts : []);
  const [error, setError] = useState(ssrError);
  const [page, setPage] = useState(0); // page 0 always represents the first batch
  const [hasMore, setHasMore] = useState(
    isInitialSSRCategory ? ssrPosts.length >= ssrLimit : true
  );
  const [loading, setLoading] = useState(false);
  const [breadCrumb, setBreadCrumb] = useState(initBread || 'videos');

  const loadRef = useRef(null);
  // If SSR already gave us page 0, mark it as fetched so fetchData skips it.
  const currentPage = useRef(isInitialSSRCategory ? 0 : -1);

  // Fetch Data
  const fetchData = useCallback(async () => {
    if (!hasMore || currentPage.current === page) return;

    currentPage.current = page;
    setLoading(true);

    try {
      const catId = pathname.substring(
        pathname.lastIndexOf("-") + 1,
        pathname.lastIndexOf(".html")
      );
      const bread = pathname.substring(
        pathname.lastIndexOf("/") + 1,
        pathname.lastIndexOf("-")
      );

      const limit = mobile ? 6 : 12;

      const res = await fetch(
        `https://www.teluguone.com/devotional_api/devotionalCatwiseVideosJson?catId=${catId}&start=${page * limit}&limit=${limit}`
      );

      if (!res.ok) throw new Error("Server response not ok");

      const json = await res.json();
      const all = json?.devotional_cat_videos || [];
      const validItems = all.filter((item) => !item.msg);
      const hasMsg = all.some((item) => item.msg);

      if (validItems.length > 0) {
        setPosts((prev) => (page === 0 ? validItems : [...prev, ...validItems]));
        setBreadCrumb(bread || "Videos");
        setHasMore(validItems.length >= limit);
      } else {
        setHasMore(false);
      }

      if (hasMsg) setHasMore(false);
    } catch (err) {
      setError(err.message);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [page, hasMore, pathname, mobile]);

  // Reset on path change
  useEffect(() => {
    if (isInitialSSRCategory) {
      setPosts(ssrPosts);
      setHasMore(ssrPosts.length >= ssrLimit);
      currentPage.current = 0; // page 0 served by SSR — don't refetch
    } else {
      setPosts([]);
      setHasMore(true);
      currentPage.current = -1; // need to fetch page 0
    }
    setPage(0);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Trigger fetches — fetchData itself guards against re-fetching page 0 when SSR served it
  useEffect(() => {
    fetchData();
  }, [page, fetchData]);

  // IntersectionObserver — sentinel must stay mounted across fetches
  useEffect(() => {
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && hasMore) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    const node = loadRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, [hasMore, loading]);

  // Initial-load skeleton only (don't unmount the sentinel during pagination)
  if (loading && posts.length === 0) {
    return (
      <div className='devotional-videolist'>
        <div className='dvl-grid'>
          {Array(12).fill("o").map((_, index) => (
            <div key={index} className="dvl-skeleton-card">
              <Skeleton type={"square"} />
              <Skeleton type={"title"} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='devotional-videolist'>
        <div className="json-loading-error-div">
          <img src="/devotional/assets/images/json_error.png" alt="json_error" />
        </div>
      </div>
    );
  }

  return (
    <div className='devotional-videolist'>
      <Helmet>
        <title>Telugu Bhakti Videos | Telugu Devotional Videos | TeluguOne Devotional Videos | Telugu Devotional Videos | TeluguOne Devotional</title>
      </Helmet>

      <DevotionalBreadCrumb type={"Video Gallery"} breadCrumb={breadCrumb} />

      <div className='dvl-grid'>
        {Array.isArray(posts) && posts.map((item, index) => {
          const href = item.video_url
            ? item.video_url.substring(item.video_url.indexOf('/devotional') + 11)
            : '#';
          return (
            <figure key={index} className="devotional-videolist-item">
              <Link to={href}>
                <div className="thumb-wrap">
                  <img
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchpriority={index === 0 ? 'high' : undefined}
                    src={item.video_image_url || '/devotional/assets/images/error_img.webp'}
                    alt={item.video_title || "No Title"}
                  />
                  <FaYoutube className='dvi-icon' />
                </div>
              </Link>
              <figcaption>
                <Link to={href}>{item.video_title || "No Title"}</Link>
              </figcaption>
            </figure>
          );
        })}
      </div>

      {hasMore && Array.isArray(posts) && posts.length > 0 ? (
        <div className='dvl-load-more' ref={loadRef}>
          {loading && (
            <div className='dvl-grid'>
              {Array(4).fill("o").map((_, index) => (
                <div key={index} className="dvl-skeleton-card">
                  <Skeleton type={"square"} />
                  <Skeleton type={"title"} />
                </div>
              ))}
            </div>
          )}
          <p className='dvl-status-text'>{loading ? 'Loading...' : 'Load More'}</p>

        </div>
      ) : (
        <p className='dvl-status-text'>No More</p>
      )}


      <PageAds  mobile={mobile} containerRef={containerRef} />
    </div>
  );
};

export default DevotionalVideoList;

